import { useEffect } from "react";
import { toast } from "sonner";
import { flushQueue } from "@/lib/offline";
import { flushEvents } from "@/lib/track";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Languages, Wrench, ClipboardCheck, MessageCircleHeart, Settings as Cog, LogOut, HelpCircle, ShieldCheck, ChevronDown } from "lucide-react";
import { Brand, Motto } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { LANGUAGE_SUBNAV, SKILLS_SUBNAV } from "@/components/SubNav";

function Dropdown({ item, cls, lang }) {
  const { to, icon: I, label, id, children } = item;
  return (
    <div className="group relative">
      <NavLink to={to} className={cls} data-testid={id}><I className="h-4 w-4" />{label}<ChevronDown className="h-3 w-3 opacity-60" /></NavLink>
      <div className="invisible absolute left-0 top-full z-40 min-w-[240px] translate-y-1 pt-2 opacity-0 transition-[opacity,transform] group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100" data-testid={`${id}-menu`}>
        <div className="soft-card overflow-hidden p-1.5 shadow-lg">
          {children.map((c) => (
            <NavLink key={c.id} to={c.to} end={c.end} data-testid={`${id}-${c.id}`} className={({ isActive }) => `flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? "bg-foreground text-background" : "hover:bg-accent"}`}>
              <span>{lang === "jp" ? c.label_jp : c.label_id}</span>
              {c.soon && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">Soon</span>}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { t, lang } = usePrefs();
  const nav = useNavigate();
  useEffect(() => { const sync = () => { flushEvents(); flushQueue().then((n) => { if (n) toast.success(`${n} jawaban offline tersinkron`); }); }; sync(); window.addEventListener("online", sync); return () => window.removeEventListener("online", sync); }, []);
  const items = [
    { to: "/dashboard", icon: Home, label: t("dashboard"), id: "nav-dashboard" },
    { to: "/settings", icon: Cog, label: t("settings"), id: "nav-settings" },
    { to: "/assessment", icon: ClipboardCheck, label: t("assessment"), id: "nav-assessment" },
    { to: "/modules", icon: Languages, label: t("modules"), short: t("modulesShort"), id: "nav-modules", children: LANGUAGE_SUBNAV },
    { to: "/skills", icon: Wrench, label: t("skills"), short: t("skillsShort"), id: "nav-skills", children: SKILLS_SUBNAV },
    { to: "/care-center", icon: MessageCircleHeart, label: t("care"), short: "Care", id: "nav-care" },
  ];
  const cls = ({ isActive }) =>
    `flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 text-sm transition-colors duration-200 ${isActive ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`;
  const mcls = ({ isActive }) =>
    `flex flex-col items-center gap-1 py-2 text-[11px] leading-tight transition-colors ${isActive ? "text-primary" : "text-muted-foreground"}`;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <Brand to="/dashboard" />
          <nav className="hidden items-center gap-1 md:flex" data-testid="desktop-nav">
            {items.map((it) => it.children ? <Dropdown key={it.to} item={it} cls={cls} lang={lang} /> : (
              <NavLink key={it.to} to={it.to} className={cls} data-testid={it.id}><it.icon className="h-4 w-4" />{it.label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            {user?.role === "superadmin" && <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) => `rounded-full p-2 transition-colors hover:bg-accent ${isActive ? "text-primary" : "text-muted-foreground"}`} aria-label="Admin"><ShieldCheck className="h-4 w-4" /></NavLink>}
            <NavLink to="/guide" data-testid="nav-guide" className={({ isActive }) => `rounded-full p-2 transition-colors hover:bg-accent ${isActive ? "text-primary" : "text-muted-foreground"}`} aria-label="Panduan"><HelpCircle className="h-4 w-4" /></NavLink>
            {user?.target_level && <span data-testid="header-level-badge" className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-semibold text-secondary dark:bg-secondary/20">{user.target_level}</span>}
            <button data-testid="logout-button" onClick={async () => { await logout(); nav("/"); }} className="hidden rounded-full p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground md:inline-flex" aria-label={t("logout")}><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 pb-28 pt-6 sm:px-6 sm:pt-10 md:pb-16">{children}<footer className="mt-16 border-t border-border/70 pt-6" data-testid="app-footer"><Motto testid="app-footer-motto" /><p className="mt-2 text-[11px] text-muted-foreground">© {new Date().getFullYear()} LPK Yutaka Education Center · Yutaka Manabi</p></footer></main>
      <nav data-testid="mobile-nav" className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-6 border-t border-border/70 bg-background/92 backdrop-blur-md md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {items.map(({ to, icon: I, label, short, id }) => (
          <NavLink key={to} to={to} className={mcls} data-testid={`${id}-mobile`}><I className="h-5 w-5" /><span className="truncate px-0.5">{short || label}</span></NavLink>
        ))}
      </nav>
    </div>
  );
}
